import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import type { CountryCodes, Market } from '@bridebook/toolbox/src/gazetteer';
import {
  IPackagesSections,
  ISupplierPackage,
} from '@bridebook/toolbox/src/map-packages-to-sections';
import { IEnrichedSupplier } from '@bridebook/toolbox/src/supplier/types';
import { Box } from '@bridebook/ui';
import ContentContainer from '@bridebook/web/app-basic/components/supplier/content-container';
import { PRICING_SECTION_ID } from '@bridebook/web/app-shared/components/supplier/sections/ids';
import PackagePricing from '@bridebook/web/app-shared/components/supplier/sections/pricing/package-pricing';
import SupplierPricing from '@bridebook/web/app-basic/components/supplier/sections/pricing/supplier-pricing';
import SupplierAccordion, {
  IAccordionProps,
} from '@bridebook/web/app-shared/components/supplier/sections/supplier-accordion/supplier-accordion';
import styles from '@bridebook/web/app-basic/components/supplier/sections/pricing/pricing.style';
import VenuePricing from '@bridebook/web/app-shared/components/supplier/sections/pricing/venue-pricing';
import { useHasPrice } from '@bridebook/web/app-basic/components/supplier/sections/pricing/useHasPrice';

export interface IProps {
  accordionProps: IAccordionProps;
  basePrice?: number;
  currency: string | null;
  info?: string;
  isLoggedIn?: boolean;
  isMobile?: boolean;
  isVenue: boolean;
  market: Market;
  maxPrice?: number;
  onShowMore?: () => void;
  openStatic?: boolean;
  packages: IPackagesSections | undefined;
  showAllInfoText: boolean;
  supplier: IEnrichedSupplier | undefined;
  supplierCountryCode?: CountryCodes;
  supplierPackages?: ISupplierPackage[];
  toggleEnquiryFormButton?: React.ReactNode;
  venuePricingHeader?: React.ReactNode;
}

const Pricing = ({
  accordionProps,
  basePrice,
  currency,
  info,
  isLoggedIn,
  isMobile = false,
  isVenue,
  market,
  maxPrice,
  onShowMore,
  openStatic,
  packages,
  showAllInfoText,
  supplier,
  supplierCountryCode,
  supplierPackages,
  toggleEnquiryFormButton,
  venuePricingHeader,
}: IProps) => {
  const { t } = useTranslation('supplier');

  const supplierName = supplier?.name;
  const hasPrice = useHasPrice();

  const text = useMemo(() => {
    if (isVenue) {
      return t('supplier.pricingWithName.accordion.venueTitle', {
        supplierName,
      });
    }
    return t('supplier.pricingWithName.accordion.title', {
      supplierName,
    });
  }, [isVenue, supplierName, t]);

  const variant = useMemo(() => {
    if (!isLoggedIn && isVenue) return 'h2';
  }, [isLoggedIn, isVenue]);

  if (!hasPrice) return null;

  return (
    <Box id={PRICING_SECTION_ID} style={styles.pricingWrap}>
      <SupplierAccordion
        {...accordionProps}
        horizontalMargins={isMobile}
        openStatic={openStatic}
        name="pricing-accordion"
        text={text}
        headCustom={null}
        disabled={false}
        hideArrow={false}
        onClick={undefined}
        variant={variant}>
        <ContentContainer noPaddingBottom={!toggleEnquiryFormButton}>
          {isVenue ? (
            <Box style={styles.venuePricingWrapper}>
              {venuePricingHeader}
              <VenuePricing
                supplier={supplier}
                packages={packages}
                onShowMore={onShowMore}
                defaultCurrency={currency}
                market={market}
                isMobile={isMobile}
              />
            </Box>
          ) : supplierPackages && supplierPackages.length > 0 ? (
            <PackagePricing
              currency={currency}
              packages={supplierPackages}
              minPrice={basePrice}
              maxPrice={maxPrice}
              description={info}
              onShowMore={onShowMore}
              market={market}
            />
          ) : (
            <SupplierPricing
              minPrice={basePrice}
              maxPrice={maxPrice}
              supplierCountryCode={supplierCountryCode}
              info={info}
              showAllInfoText={showAllInfoText}
              market={market}
            />
          )}
          {!!toggleEnquiryFormButton && (
            <Box style={styles.enquiryWrap}>{toggleEnquiryFormButton}</Box>
          )}
        </ContentContainer>
      </SupplierAccordion>
    </Box>
  );
};

export default Pricing;
